import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'
import { useFormik } from 'formik'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'

import logo from 'shared/img/logo.webp'

import { localeAuthKeysType } from 'shared/locale/en-US'

import { signIn } from 'features/auth/actions'
import { selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { changePassword } from 'features/profile/actions'
import { selectForceChangePasswordCommunication } from 'features/profile/selectors'
import { selectRequirePasswordChangeEnabled } from 'features/settings/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import Button from 'components/UIKit/Button/Button'
import { Input } from 'components/UIKit/Input/Input'
import { Portal } from 'components/UIKit/Portal'

import { ChangeForcePasswordSchema, initForceChangePasswordValues } from './MandatoryChangePasswordOverlay.utils'

import './MandatoryChangePasswordOverlay.scss'

const b = block('mandatory-change-password-overlay')

export const MandatoryChangePasswordOverlay: React.FC = () => {
  const dispatch = useDispatch()
  const { pushNewModal } = useModalContext()

  const user = useSelector(selectUserData, shallowEqual)
  const requirePasswordChangeEnabled = useSelector(selectRequirePasswordChangeEnabled, shallowEqual)
  const { isLoading } = useSelector(selectForceChangePasswordCommunication, shallowEqual)
  const { profile, auth } = useSelector(selectLocaleDict, shallowEqual)

  const formik = useFormik({
    initialValues: initForceChangePasswordValues,
    validationSchema: ChangeForcePasswordSchema,
    onSubmit: values => {
      if (!user) return
      
      dispatch(changePassword(
        {
          data: {
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
            repeatNewPassword: values.repeatNewPassword
          },
          callback: () => {
            dispatch(signIn(
              { username: user.username, password: values.newPassword },
              { callback: pushNewModal(MODAL_ROUTE_URLS.SUCCESS_CHANGED_PASSWORD()) }
            ))
          }
        },
      ))
    },
  })

  if (!(requirePasswordChangeEnabled && user?.requirePasswordChange)) {
    return null
  }
  
  return (
    <Portal id="modal-root">
      <div className={b('container')}>
        <div className={b('overlay')}>
          <section className={b()}>
            <div className={b('header')}>{auth.updateYourPassword}</div>
            <div className={b('description')}>
              {auth.updatePasswordDescription}
            </div>
            <form className={b('form')} onSubmit={formik.handleSubmit}>
              <div className={b('input-group')}>
                <span className={b('input-label')}>
                  {profile.enterOldPassword}
                </span>
                <Input
                  value={formik.values.oldPassword}
                  name="oldPassword"
                  type="password"
                  onBlur={formik.handleBlur}
                  onChange={e => formik.setFieldValue('oldPassword', e.currentTarget.value)}
                  placeholder={profile.enterOldPassword}
                  height='52px'
                  borderPar={formik.touched.oldPassword && formik.errors.oldPassword
                    ? '1px solid #72232D'
                    : undefined
                  }
                />
                {formik.touched.oldPassword && formik.errors.oldPassword && (
                  <span className={b('error')}>
                    {auth[formik.errors.oldPassword as localeAuthKeysType]}
                  </span>
                )}
              </div>
              <div className={b('input-group')}>
                <span className={b('input-label')}>
                  {profile.enterNewPassword}
                </span>
                <Input
                  value={formik.values.newPassword}
                  name="newPassword"
                  type="password"
                  onBlur={formik.handleBlur}
                  onChange={e => formik.setFieldValue('newPassword', e.currentTarget.value.trim())}
                  placeholder={profile.enterNewPassword}
                  height='52px'
                  borderPar={formik.touched.newPassword && formik.errors.newPassword
                    ? '1px solid #72232D'
                    : undefined
                  }
                />
                {formik.touched.newPassword && formik.errors.newPassword && (
                  <span className={b('error')}>
                    {auth[formik.errors.newPassword as localeAuthKeysType]}
                  </span>
                )}
              </div>
              <div className={b('input-group')}>
                <span className={b('input-label')}>
                  {profile.repeatNewPassword}
                </span>
                <Input
                  value={formik.values.repeatNewPassword}
                  name="repeatNewPassword"
                  type="password"
                  onBlur={formik.handleBlur}
                  onChange={e => formik.setFieldValue('repeatNewPassword', e.currentTarget.value.trim())}
                  placeholder={profile.repeatNewPassword}
                  height='52px'
                  borderPar={formik.touched.repeatNewPassword && formik.errors.repeatNewPassword
                    ? '1px solid #72232D'
                    : undefined
                  }
                />
                {formik.touched.repeatNewPassword && formik.errors.repeatNewPassword && (
                  <span className={b('error')}>
                    {auth[formik.errors.repeatNewPassword as localeAuthKeysType]}
                  </span>
                )}
              </div>
              <div className={b('button-container')}>
                <Button
                  loading={isLoading}
                  disabled={isLoading || !formik.isValid}
                  text={profile.save}
                  variant="main"
                  type="submit"
                />
              </div>
            </form>
            <div className={b('separator')} />
            <img src={logo} alt='' className={b('logo')} />
          </section>
        </div>
      </div>
    </Portal>
  )
}
